import React from 'react'
import { Box, Checkbox, Input, Text } from '@chakra-ui/react'
import {
  FormatType,
  INPUT_ATTRIBUTES_CONFIG,
  OUTPUT_ATTRIBUTES_CONFIG,
  FormatAttributeConfig
} from './PersistenceProcessDialogConfigs'
import { OSTooltip } from '../configmenus/MenuCommon'
import { MenuCondition, MenuConditionEvaluator } from './SankeyMenuContext'
import { Class_ApplicationData } from '../../types/ApplicationData'

interface FormatCheckboxProps<F extends FormatType> {
  format: F
  propertyName: string
  value: boolean
  onChange: (checked: boolean) => void
  lang: 'en' | 'fr'
  config: typeof OUTPUT_ATTRIBUTES_CONFIG | typeof INPUT_ATTRIBUTES_CONFIG
}

export const FormatCheckbox = <F extends FormatType>({
  format,
  propertyName,
  value,
  onChange,
  lang,
  config
}: FormatCheckboxProps<F>) => {
  const formatConfig = config[format]
  
  // @ts-expect-error Type inference limitation
  const attr = formatConfig[propertyName] as FormatAttributeConfig<boolean>
  
  return (
    <Checkbox
      variant='menuconfigpanel_option_checkbox'
      isChecked={value}
      onChange={(e) => onChange(e.target.checked)}
    >
      <OSTooltip label={attr.tooltips[lang]}>
        {attr.labels[lang]}
      </OSTooltip>
    </Checkbox>
  )
}

interface AutoGeneratedOptionsProps<F extends FormatType> {
  app_data:Class_ApplicationData,
  format: F
  config: typeof OUTPUT_ATTRIBUTES_CONFIG | typeof INPUT_ATTRIBUTES_CONFIG
  options: Record<string, unknown>
  setOptions: (options: Record<string, unknown>) => void
  lang: 'en' | 'fr'
}

export const AutoGeneratedOptions = <F extends FormatType>({
  app_data,
  format,
  config,
  options,
  setOptions,
  lang
}: AutoGeneratedOptionsProps<F>) => {
  const formatConfig = config[format]
  
  // Fonction pour vérifier si un attribut doit être affiché
  const shouldShowAttribute = (attr: unknown): boolean => {
    const typedAttr = attr as { visibilityConditions?: MenuCondition[] }
    if (!typedAttr.visibilityConditions) return true
    
    return MenuConditionEvaluator.evaluateAll(
      typedAttr.visibilityConditions,
      app_data,
      options as Record<string, unknown>
    )
  }
  
  // Premier passage : identifier les groupes parent-enfants
  const entries = Object.entries(formatConfig)
  const processed = new Set<string>()
  const groups: Array<{ parent: string; children: string[] }> = []
  
  entries.forEach(([key, _attrValue]) => {
    if (processed.has(key)) return
  
    // Chercher les enfants de cet attribut
    const children: string[] = []
    entries.forEach(([childKey, childAttrValue]) => {
      if (childKey === key) return
      
      const childAttr = childAttrValue as FormatAttributeConfig<boolean | number | string>
      if (!childAttr.visibilityConditions) return
      
      // Vérifier si ce child dépend de key
      const dependsOnKey = childAttr.visibilityConditions.some(
        condition => condition.type === 'optionProperty' && condition.property === key
      )
      
      if (dependsOnKey) {
        children.push(childKey)
        processed.add(childKey)
      }
    })
    
    groups.push({ parent: key, children })
    processed.add(key)
  })
  
  // Fonction pour rendre un attribut
  const renderAttribute = (key: string, attr: FormatAttributeConfig<boolean | number | string>) => {
    const typedKey = key
    
    // Pour les boolean
    if (typeof attr.default === 'boolean') {
      return (
        <FormatCheckbox
          format={format}
          propertyName={typedKey}
          value={(options)[key] as boolean}
          onChange={(checked) => {
            setOptions({
              ...options,
              [key]: checked
            })
          }}
          lang={lang}
          config={config}
        />
      )
    }
    
    // Pour les numbers
    if (typeof attr.default === 'number') {
      return (
        <Box layerStyle='menuconfigpanel_row_2cols'>
          <Text>{attr.labels[lang]}</Text>
          <OSTooltip label={attr.tooltips[lang]}>
            <Input
              type="number"
              value={(options as Record<string, unknown>)[key] as number}
              onChange={(e) => {
                setOptions({
                  ...options,
                  [key]: parseInt(e.target.value)
                })
              }}
              size='sm'
              width='100px'
            />
          </OSTooltip>
        </Box>
      )
    }
    
    return null
  }
  
  // Deuxième passage : rendre les groupes
  return (
    <>
      {groups.map((group) => {
        //@ts-expect-error xxx
        const parentAttr = formatConfig[group.parent] as FormatAttributeConfig<boolean | number | string>
        
        // Vérifier si le parent doit être affiché
        if (!shouldShowAttribute(parentAttr)) return null
        
        // Filtrer les enfants visibles
        const visibleChildren = group.children.filter(childKey => {
          //@ts-expect-error xxx
          const childAttr = formatConfig[childKey]
          return shouldShowAttribute(childAttr)
        })
        
        // Si pas d'enfants visibles, affichage simple
        if (visibleChildren.length === 0) {
          return (
            <Box key={group.parent}>
              {renderAttribute(group.parent, parentAttr)}
            </Box>
          )
        }
        
        // Affichage groupé : parent + enfants sur la même ligne
        return (
          <Box layerStyle='menuconfigpanel_row_2cols'>
            {/* Parent */}
            {renderAttribute(group.parent, parentAttr)}
            
            {/* Enfants */}
            {visibleChildren.map(childKey => {
              //@ts-expect-error xxx
              const childAttr = formatConfig[childKey] as FormatAttributeConfig<boolean | number | string>
              return renderAttribute(childKey, childAttr)
            })}
          </Box>
        )
      })}
    </>
  )
}